import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, Loader2, RotateCcw, StickyNote } from 'lucide-react';
import { type FormEvent, type KeyboardEvent, useMemo, useRef, useState } from 'react';

import { cn } from '../../shared/lib/cn';
import { Button } from '../../shared/ui/Button';

import {
  BATCH_ATTENDANCE_QUERY_KEYS,
  clearDailyAttendance,
  type DailyAttendanceDay,
  type DailyAttendanceRow,
  type DailyAttendanceStatus,
  markDailyAttendance,
} from './api';
import s from './AttendancePanel.module.css';

const STATUS_OPTIONS: { value: DailyAttendanceStatus; label: string }[] = [
  { value: 'present', label: 'Present' },
  { value: 'absent', label: 'Absent' },
  { value: 'late', label: 'Late' },
  { value: 'excused', label: 'Excused' },
];

const SELECTED_CLASS: Record<DailyAttendanceStatus, string> = {
  present: s.optPresent ?? '',
  absent: s.optAbsent ?? '',
  late: s.optLate ?? '',
  excused: s.optExcused ?? '',
};

interface Props {
  batchId: string;
  date: string; // YYYY-MM-DD
  day: DailyAttendanceDay;
}

interface MutationVars {
  studentIds: string[];
  /** `null` erases the mark — the segmented control toggles off on re-click. */
  status: DailyAttendanceStatus | null;
}

interface MutationContext {
  previous: DailyAttendanceDay | undefined;
}

interface NoteVars {
  studentId: string;
  status: DailyAttendanceStatus;
  notes: string | null;
}

export function DailyAttendancePanel({ batchId, date, day }: Props): JSX.Element {
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<ReadonlySet<string>>(new Set());
  const [recentlySaved, setRecentlySaved] = useState<ReadonlySet<string>>(new Set());
  const [errors, setErrors] = useState<ReadonlyMap<string, string>>(new Map());

  const rows = day.rows;

  const counts = useMemo(() => {
    const c = { present: 0, absent: 0, late: 0, excused: 0, unmarked: 0 };
    for (const r of rows) {
      if (r.status === null) c.unmarked += 1;
      else c[r.status] += 1;
    }
    return c;
  }, [rows]);

  const queryKey = BATCH_ATTENDANCE_QUERY_KEYS.day(batchId, date);

  function withSet(prev: ReadonlySet<string>, mut: (next: Set<string>) => void): Set<string> {
    const next = new Set(prev);
    mut(next);
    return next;
  }
  function withMap<K, V>(prev: ReadonlyMap<K, V>, mut: (next: Map<K, V>) => void): Map<K, V> {
    const next = new Map(prev);
    mut(next);
    return next;
  }

  const mutation = useMutation<unknown, Error, MutationVars, MutationContext>({
    mutationFn: async ({ studentIds, status }) => {
      if (status === null) {
        await clearDailyAttendance(batchId, date, studentIds);
        return;
      }
      // mark_daily_attendance upserts the whole row (`set notes = excluded.notes`),
      // so an assignment that omits `notes` ERASES any note already saved for
      // that student. Carry the current note through on every status change.
      const noteFor = (studentId: string): string | null =>
        rows.find((r) => r.student_id === studentId)?.notes ?? null;
      const assignments = studentIds.map((student_id) => ({
        student_id,
        status,
        notes: noteFor(student_id),
      }));
      await markDailyAttendance(batchId, date, assignments);
    },
    onMutate: async ({ studentIds, status }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<DailyAttendanceDay>(queryKey);
      setPending((p) =>
        withSet(p, (next) => {
          for (const id of studentIds) next.add(id);
        }),
      );
      setErrors((e) =>
        withMap(e, (next) => {
          for (const id of studentIds) next.delete(id);
        }),
      );
      if (previous !== undefined) {
        const idSet = new Set(studentIds);
        const optimistic: DailyAttendanceDay = {
          ...previous,
          // Clearing deletes the whole row server-side, notes included.
          rows: previous.rows.map((r) =>
            idSet.has(r.student_id) ? { ...r, status, notes: status === null ? null : r.notes } : r,
          ),
        };
        queryClient.setQueryData<DailyAttendanceDay>(queryKey, optimistic);
      }
      return { previous };
    },
    onError: (err, { studentIds }, ctx) => {
      if (ctx?.previous !== undefined) {
        queryClient.setQueryData(queryKey, ctx.previous);
      }
      const message = err.message === '' ? 'Save failed' : err.message;
      setPending((p) =>
        withSet(p, (next) => {
          for (const id of studentIds) next.delete(id);
        }),
      );
      setErrors((e) =>
        withMap(e, (next) => {
          for (const id of studentIds) next.set(id, message);
        }),
      );
    },
    onSuccess: (_d, { studentIds }) => {
      setPending((p) =>
        withSet(p, (next) => {
          for (const id of studentIds) next.delete(id);
        }),
      );
      setRecentlySaved((r) =>
        withSet(r, (next) => {
          for (const id of studentIds) next.add(id);
        }),
      );
      window.setTimeout(() => {
        setRecentlySaved((r) =>
          withSet(r, (next) => {
            for (const id of studentIds) next.delete(id);
          }),
        );
      }, 1500);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey });
      // The month register for this batch now disagrees with what was just
      // saved; prefix-invalidate every cached month so Day → Month doesn't
      // show stale cells.
      void queryClient.invalidateQueries({
        queryKey: ['institute', 'batch_monthly_view', batchId],
      });
    },
  });

  // Notes ride a separate mutation: MutationVars fans one status out across
  // many students, which cannot express a per-student note.
  const noteMutation = useMutation<unknown, Error, NoteVars, MutationContext>({
    mutationFn: async ({ studentId, status, notes }) => {
      await markDailyAttendance(batchId, date, [{ student_id: studentId, status, notes }]);
    },
    onMutate: async ({ studentId, notes }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<DailyAttendanceDay>(queryKey);
      setPending((p) => withSet(p, (next) => next.add(studentId)));
      setErrors((e) => withMap(e, (next) => next.delete(studentId)));
      if (previous !== undefined) {
        queryClient.setQueryData<DailyAttendanceDay>(queryKey, {
          ...previous,
          rows: previous.rows.map((r) => (r.student_id === studentId ? { ...r, notes } : r)),
        });
      }
      return { previous };
    },
    onError: (err, { studentId }, ctx) => {
      if (ctx?.previous !== undefined) queryClient.setQueryData(queryKey, ctx.previous);
      setPending((p) => withSet(p, (next) => next.delete(studentId)));
      setErrors((e) =>
        withMap(e, (next) => next.set(studentId, err.message === '' ? 'Save failed' : err.message)),
      );
    },
    onSuccess: (_d, { studentId }) => {
      setPending((p) => withSet(p, (next) => next.delete(studentId)));
      setRecentlySaved((r) => withSet(r, (next) => next.add(studentId)));
      window.setTimeout(() => {
        setRecentlySaved((r) => withSet(r, (next) => next.delete(studentId)));
      }, 1500);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey });
      void queryClient.invalidateQueries({
        queryKey: ['institute', 'batch_monthly_view', batchId],
      });
    },
  });

  // Re-clicking the option a student already has erases the mark, so a
  // mis-click is undoable rather than only overwritable. Same escape hatch the
  // grid gets at the end of its P → A → L → E cycle.
  function setStatus(
    studentId: string,
    status: DailyAttendanceStatus,
    current: DailyAttendanceStatus | null,
  ): void {
    mutation.mutate({ studentIds: [studentId], status: status === current ? null : status });
  }

  function saveNote(row: DailyAttendanceRow, notes: string): void {
    // The RPC requires a status, so a note can only attach to a marked student.
    if (row.status === null) return;
    const trimmed = notes.trim();
    noteMutation.mutate({
      studentId: row.student_id,
      status: row.status,
      notes: trimmed === '' ? null : trimmed,
    });
  }

  function markAllPresent(): void {
    const ids = rows.filter((r) => r.status === null).map((r) => r.student_id);
    if (ids.length === 0) return;
    mutation.mutate({ studentIds: ids, status: 'present' });
  }

  const unsetCount = counts.unmarked;
  const bulkBusy =
    mutation.isPending &&
    mutation.variables !== undefined &&
    mutation.variables.studentIds.length === unsetCount &&
    mutation.variables.status === 'present';

  return (
    <div className={s.root}>
      <div className={s.toolbar} role="toolbar" aria-label="Attendance summary">
        <ul className={s.summary} aria-live="polite">
          <li>
            <span className={cn(s.dot, s.dotPresent)} aria-hidden="true" />
            {counts.present} Present
          </li>
          <li>
            <span className={cn(s.dot, s.dotAbsent)} aria-hidden="true" />
            {counts.absent} Absent
          </li>
          <li>
            <span className={cn(s.dot, s.dotLate)} aria-hidden="true" />
            {counts.late} Late
          </li>
          <li>
            <span className={cn(s.dot, s.dotExcused)} aria-hidden="true" />
            {counts.excused} Excused
          </li>
          <li className={s.summaryRemaining}>
            <span className={cn(s.dot, s.dotInvited)} aria-hidden="true" />
            {counts.unmarked} Unmarked
          </li>
        </ul>
        {unsetCount > 0 ? (
          <Button
            variant="secondary"
            className={s.bulkBtn}
            onClick={markAllPresent}
            loading={bulkBusy}
            aria-label={`Mark remaining ${unsetCount} ${
              unsetCount === 1 ? 'student' : 'students'
            } present`}
          >
            Mark all present ({unsetCount})
          </Button>
        ) : null}
      </div>

      {rows.length === 0 ? (
        <p className={s.empty}>No students are assigned to this batch yet.</p>
      ) : (
        <ul className={s.list}>
          {rows.map((row) => (
            <DailyAttendanceRowItem
              key={row.student_id}
              row={row}
              pending={pending.has(row.student_id)}
              justSaved={recentlySaved.has(row.student_id)}
              error={errors.get(row.student_id) ?? null}
              onChange={(status) => setStatus(row.student_id, status, row.status)}
              onSaveNote={(notes) => saveNote(row, notes)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

interface RowProps {
  row: DailyAttendanceRow;
  pending: boolean;
  justSaved: boolean;
  error: string | null;
  onChange: (status: DailyAttendanceStatus) => void;
  onSaveNote: (notes: string) => void;
}

function DailyAttendanceRowItem({
  row,
  pending,
  justSaved,
  error,
  onChange,
  onSaveNote,
}: RowProps): JSX.Element {
  const groupRef = useRef<HTMLDivElement | null>(null);
  const noteInputRef = useRef<HTMLInputElement | null>(null);
  const [noteOpen, setNoteOpen] = useState(false);
  const [draft, setDraft] = useState(row.notes ?? '');
  const labelName = row.display_name === '' ? row.email : row.display_name;
  const hasNote = row.notes !== null && row.notes !== '';
  // mark_daily_attendance needs a status, so there is nothing to attach a note
  // to until the student has been marked.
  const noteDisabled = row.status === null;

  function openNote(): void {
    setDraft(row.notes ?? '');
    setNoteOpen(true);
    // Focus is moved deliberately in response to the user's click rather than
    // via autoFocus, which fires on mount regardless of intent.
    window.requestAnimationFrame(() => noteInputRef.current?.focus());
  }

  function commitNote(e: FormEvent): void {
    e.preventDefault();
    if (draft !== (row.notes ?? '')) onSaveNote(draft);
    setNoteOpen(false);
  }

  function handleNoteKey(e: KeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'Escape') {
      e.preventDefault();
      setDraft(row.notes ?? '');
      setNoteOpen(false);
    }
  }
  const focusedIndex =
    row.status === null
      ? 0
      : Math.max(
          0,
          STATUS_OPTIONS.findIndex((o) => o.value === row.status),
        );

  function handleKey(e: KeyboardEvent<HTMLDivElement>): void {
    const buttons = groupRef.current?.querySelectorAll<HTMLButtonElement>('[role="radio"]');
    if (buttons === undefined || buttons.length === 0) return;
    const list = Array.from(buttons);
    const idx = list.findIndex((b) => b === document.activeElement);
    if (idx < 0) return;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      list[(idx + 1) % list.length]?.focus();
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      list[(idx - 1 + list.length) % list.length]?.focus();
    }
  }

  return (
    // `rowStacked` opts this row into the narrow-screen stacked layout. The
    // session AttendancePanel shares this stylesheet but not this markup, so the
    // reflow must not be keyed off `.row` alone.
    <li className={cn(s.row, s.rowStacked)}>
      <div className={s.rowMain}>
        <span className={s.rowName}>{labelName}</span>
        <span className={s.rowMeta}>
          {row.email}
          {row.roll_no !== null && row.roll_no !== '' ? ` · roll ${row.roll_no}` : ''}
        </span>
        {hasNote && !noteOpen ? <span className={s.noteText}>“{row.notes}”</span> : null}
      </div>

      <div className={s.controls}>
        <div
          ref={groupRef}
          role="radiogroup"
          aria-label={`Attendance for ${labelName}`}
          tabIndex={-1}
          className={s.segmented}
          onKeyDown={handleKey}
        >
          {STATUS_OPTIONS.map((opt, i) => {
            const selected = opt.value === row.status;
            // Phase 135: the nightly job, not a person, put this student here.
            const isAutoAbsent = selected && opt.value === 'absent' && row.auto_marked === true;
            return (
              <button
                key={opt.value}
                type="button"
                role="radio"
                aria-checked={selected}
                // Visible text stays plain ("Absent") so the option list never
                // reflows; the distinction rides the title and, for screen
                // reader users, an explicit aria-label override.
                aria-label={isAutoAbsent ? `${opt.label} — auto-marked, unconfirmed` : undefined}
                tabIndex={i === focusedIndex ? 0 : -1}
                disabled={pending}
                title={
                  isAutoAbsent
                    ? 'Auto-marked absent — nobody recorded this day. Click again to clear, or pick another status.'
                    : selected
                      ? `${opt.label} — click again to clear`
                      : opt.label
                }
                className={cn(
                  s.opt,
                  isAutoAbsent
                    ? s.optAbsentAuto
                    : selected
                      ? // eslint-disable-next-line security/detect-object-injection -- opt.value is the DailyAttendanceStatus enum
                        SELECTED_CLASS[opt.value]
                      : null,
                )}
                onClick={() => onChange(opt.value)}
              >
                {opt.label}
              </button>
            );
          })}
        </div>

        <button
          type="button"
          className={cn(s.noteBtn, hasNote ? s.noteBtnFilled : null)}
          disabled={noteDisabled || pending}
          aria-expanded={noteOpen}
          title={
            noteDisabled
              ? 'Mark this student first, then add a note'
              : hasNote
                ? (row.notes ?? '')
                : 'Add a note'
          }
          aria-label={`${hasNote ? 'Edit' : 'Add'} note for ${labelName}`}
          onClick={() => (noteOpen ? setNoteOpen(false) : openNote())}
        >
          <StickyNote size={15} aria-hidden="true" />
        </button>
      </div>

      <span className={s.status} aria-live="polite">
        {pending ? (
          <>
            <Loader2 size={14} className={s.spin} aria-hidden="true" />
            <span className={s.statusText}>Saving…</span>
          </>
        ) : error !== null ? (
          <span className={s.statusError} role="alert">
            <RotateCcw size={14} aria-hidden="true" /> Retry
          </span>
        ) : justSaved ? (
          <>
            <Check size={14} className={s.savedIcon} aria-hidden="true" />
            <span className={s.statusText}>Saved</span>
          </>
        ) : null}
      </span>

      {noteOpen ? (
        <form className={s.noteForm} onSubmit={commitNote}>
          <label className={s.noteLabel} htmlFor={`note-${row.student_id}`}>
            Note
          </label>
          <input
            id={`note-${row.student_id}`}
            ref={noteInputRef}
            className={s.noteInput}
            value={draft}
            maxLength={200}
            placeholder="Reason for absence, left early…"
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleNoteKey}
            onBlur={commitNote}
          />
          <span className={s.noteHint}>Enter to save · Esc to cancel</span>
        </form>
      ) : null}
    </li>
  );
}
